import type { QueuedTransaction, RejectedRow, SendOutcome, SyncTransport } from './types'

/**
 * How each transaction type reaches the API.
 *
 * There is no general `/sync` endpoint on pallets and there will not be — the
 * API deleted it deliberately (see PalletController: producer data arrives as
 * promoted documents, and "the commands below are for people"). So each type
 * declares its own request shape and how to read that endpoint's own answer.
 *
 * A type with `live: true` goes over HTTP even while FAKE_BACKEND is on; the
 * rest keep riding the stand-in until their endpoint is decided.
 */
export type SyncSender = {
  /** Static path, or built from the transaction (the order routes are keyed). */
  path: string | ((transactions: QueuedTransaction[]) => string)
  method?: 'POST' | 'PUT'
  /** Real endpoint, not a placeholder — send it for real. */
  live?: boolean
  /**
   * One HTTP request per transaction. The pallet commands are keyed by ORDER, so
   * two load events cannot share a request.
   */
  perTransaction?: boolean
  body: (transactions: QueuedTransaction[]) => unknown
  /**
   * Translate the endpoint's own refusal shape into the queue's rejected rows.
   * Without this a 200 carrying refusals would be read as "all accepted" and
   * the operator's scan would disappear.
   */
  rejections?: (body: unknown, transactions: QueuedTransaction[]) => RejectedRow[]
}

type PalletMovePayload = {
  palletIdentifiers?: string[]
  inventoryLocationId?: string | null
  /** Whose each pallet is, where the tablet knows: the lookup said, or the driver picked (task 2221). */
  palletPartners?: Record<string, number>
  /**
   * The container the driver was working in (Mark, 2026-10-15). A pallet the
   * data has in another container gets "Wrong Container" in its history.
   */
  foundInContainer?: string | null
}

type OrderCompletePayload = {
  partnerId?: number | null
  orderNumber?: string
}

type TruckLoadPayload = {
  palletIdentifiers?: string[]
  partnerId?: number | null
  orderNumber?: string
}

type TruckUnloadPayload = TruckLoadPayload & {
  inventoryLocationId?: string | null
}

/** What POST /api/pallets/ship answers with. */
type PalletActionResponse = {
  errors?: Array<{ palletIdentifier?: string, message?: string }>
}

export const SYNC_SENDERS: Record<string, SyncSender> = {
  'truck-load': {
    // The batch ship command: these pallets went out on this order.
    path: '/api/pallets/ship',
    live: true,
    perTransaction: true,
    body: (transactions) => {
      const payload = transactions[0]!.payload as TruckLoadPayload
      return {
        palletIdentifiers: payload.palletIdentifiers ?? [],
        partnerId: payload.partnerId,
        orderNumber: payload.orderNumber,
      }
    },
    rejections: palletActionRejections,
  },
  'truck-unload': {
    // The batch unship command: these pallets came off this order, together,
    // to this floor location. Order-scoped and all-or-nothing on the server;
    // a stray pallet or an out-of-sequence order comes back as a 409, which
    // the flush engine routes to needs-attention like any other refusal.
    path: '/api/pallets/unship',
    live: true,
    perTransaction: true,
    body: (transactions) => {
      const payload = transactions[0]!.payload as TruckUnloadPayload
      return {
        palletIdentifiers: payload.palletIdentifiers ?? [],
        partnerId: payload.partnerId,
        orderNumber: payload.orderNumber,
        inventoryLocationId: payload.inventoryLocationId,
      }
    },
    rejections: palletActionRejections,
  },
  'order-complete': {
    // NOTHING ENQUEUES THIS ANY MORE (decision 0012): completing an order is
    // the office's job, not the forklift's, and Load Truck no longer offers
    // it. The sender is kept so a tablet that queued a completion before
    // that change still drains it rather than stranding it as "no endpoint"
    // — a fact the operator recorded under the old rules is still a fact.
    // Remove once every tablet has been on a build without the button.
    //
    // The order's own status transition. The path carries the target status;
    // the body must repeat partner and order or the controller 404s on the
    // mismatch it checks for.
    path: (transactions) => {
      const payload = transactions[0]!.payload as OrderCompletePayload
      return `/api/orders/${payload.partnerId}`
        + `/${encodeURIComponent(payload.orderNumber ?? '')}/${ORDER_FINISHED_STATUS}`
    },
    method: 'PUT',
    live: true,
    perTransaction: true,
    body: (transactions) => {
      const payload = transactions[0]!.payload as OrderCompletePayload
      return {
        partnerId: payload.partnerId,
        orderNumber: payload.orderNumber,
        orderStatus: ORDER_FINISHED_STATUS,
      }
    },
  },
  'pallet-move': {
    // The batch move command. The floor is addressed by id, not by name — a
    // location name is only unique per warehouse.
    path: '/api/pallets/move',
    live: true,
    perTransaction: true,
    body: (transactions) => {
      const transaction = transactions[0]!
      const payload = transaction.payload as PalletMovePayload
      return {
        palletIdentifiers: payload.palletIdentifiers ?? [],
        inventoryLocationId: payload.inventoryLocationId,
        palletPartners: payload.palletPartners ?? {},
        // Left out on the floor: the server reads no container as none.
        ...(payload.foundInContainer ? { foundInContainer: payload.foundInContainer } : {}),
        // A pallet two partners hold that nobody said whose is sent to a clerk
        // in BOB (Moves to Confirm, task 2221), never refused: by the time the
        // server sees a move the pallet has been moved, and the driver may be
        // anywhere. The move's own id lists it once however often it is sent,
        // and the driver's time is the time it is recorded at.
        confirmIfShared: true,
        externalId: transaction.id,
        occurredAtUtc: transaction.occurredAtUtc,
      }
    },
    rejections: palletActionRejections,
  },
}

/**
 * Pallet commands refuse per PALLET inside a 200; the queue tracks per
 * TRANSACTION. One bad pallet therefore sends the whole transaction to
 * needs-attention, which is safe to retry: re-applying a move or a ship that
 * already landed comes back Unchanged rather than as a duplicate.
 */
function palletActionRejections(body: unknown, transactions: QueuedTransaction[]): RejectedRow[] {
  const errors = (body as PalletActionResponse | undefined)?.errors ?? []
  if (errors.length === 0) return []
  return [{
    externalId: transactions[0]!.id,
    reason: errors
      .map(e => `${e.palletIdentifier ?? 'pallet'}: ${e.message ?? 'refused'}`)
      .join('; '),
    // Per pallet as well, so a screen can put each refusal on its fork.
    refusals: errors.map(e => ({ item: e.palletIdentifier ?? '', reason: e.message ?? 'Refused' })),
  }]
}

/**
 * The status a finished truck lands on. It is what stamps CheckedOutDate, and
 * it is the stevedore's half of the job — the partner checks out separately.
 * Note the API's WarehouseCheckedOut branch ignores Dock, so the door the load
 * left from is deliberately not sent: it would read as recorded when it is not.
 */
export const ORDER_FINISHED_STATUS = 'WarehouseCheckedOut'

/** Types that talk to a real endpoint today, whatever FAKE_BACKEND says. */
export function isLiveType(type: string): boolean {
  return SYNC_SENDERS[type]?.live === true
}

/** The body each `/sync` record carries. */
export function toSyncRecord(transaction: QueuedTransaction): Record<string, unknown> {
  return {
    // The client-minted external key the endpoint upserts on — this is what
    // makes a replayed batch a no-op instead of a duplicate.
    externalId: transaction.id,
    occurredAtUtc: transaction.occurredAtUtc,
    // Who actually performed it. How the API consumes this is PRD Open
    // Question 1 (a late flush must not credit whoever is signed in now).
    performedByEmployeeNumber: transaction.actor.employeeNumber ?? null,
    performedByName: transaction.actor.name ?? null,
    ...(transaction.payload as Record<string, unknown>),
  }
}

/** Carries the log-in a transaction was recorded under, when it is not the live one. */
export const RECORDED_SESSION_HEADER = 'X-Recorded-Session'

/**
 * The log-in headers for one send: the live log-in always, and — when the work
 * was recorded under a different log-in, one that has since run out — that
 * one beside it, so the server credits the driver who did the work rather
 * than whoever is logged in now. All the live senders send one transaction
 * per request, and the flush engine never puts two log-ins in one batch.
 */
export function sessionHeaders(liveToken: string | null, transactions: QueuedTransaction[]): Record<string, string> {
  const headers: Record<string, string> = liveToken ? { Authorization: `Bearer ${liveToken}` } : {}
  const recorded = transactions[0]?.recordedSession
  if (recorded && recorded !== liveToken) headers[RECORDED_SESSION_HEADER] = recorded
  return headers
}

export type HttpTransportOptions = {
  /** Absolute or proxy-relative API base (''=same origin, which dev proxies). */
  apiBase?: string
  /** Auth header(s) for the request, which may depend on what is being sent. */
  headers: (transactions: QueuedTransaction[]) => Record<string, string>
  /** Told about every attempt so connectivity tracks real outcomes. */
  onResult?: (reachedServer: boolean) => void
  fetchImpl?: typeof fetch
}

/**
 * Posts a batch to its `/sync` endpoint and reduces the result to a
 * `SendOutcome`. It never throws for an HTTP status — classification is the
 * flush engine's job, and it needs the status to decide.
 */
export function createHttpTransport(options: HttpTransportOptions): SyncTransport {
  const { apiBase = '', headers, onResult, fetchImpl = fetch } = options

  async function post(sender: SyncSender, transactions: QueuedTransaction[]): Promise<SendOutcome> {
    try {
      const path = typeof sender.path === 'function' ? sender.path(transactions) : sender.path
      const response = await fetchImpl(`${apiBase}${path}`, {
        method: sender.method ?? 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers(transactions) },
        body: JSON.stringify(sender.body(transactions)),
      })

      let body: unknown
      try {
        body = await response.json()
      } catch {
        if (response.ok && response.status !== 204 && !answersJson(response)) {
          // A 2xx whose body is not JSON was not the API answering: a captive
          // portal's sign-in page, or the page's own server handing back the
          // app shell for a URL it does not serve. Nothing was recorded, so
          // this is NOT acceptance — the work stays pending and is retried,
          // and the connectivity monitor hears that the API was not reached.
          onResult?.(false)
          return {
            status: null,
            message: 'The server answered, but not as the API (not JSON) — a captive portal, or the wrong address',
          }
        }
        body = undefined // 204s and empty bodies are fine
      }

      onResult?.(true) // an answer of any kind means the API is reachable

      if (response.ok && sender.rejections) {
        const rejected = sender.rejections(body, transactions)
        // Re-shaped into the queue's own envelope so classify stays generic.
        return { status: response.status, body: { rejected } }
      }

      return { status: response.status, body }
    } catch (error) {
      onResult?.(false)
      return {
        status: null,
        message: error instanceof Error ? error.message : 'Network request failed',
      }
    }
  }

  /** Whether a response declares a JSON body; unknown (no headers) passes. */
  function answersJson(response: Response): boolean {
    const contentType = response.headers?.get?.('content-type')
    return typeof contentType !== 'string' || /json/i.test(contentType)
  }

  return async (type, batch): Promise<SendOutcome> => {
    const sender = SYNC_SENDERS[type]
    if (!sender) {
      // A permanent, loud failure: queueing a type nothing can deliver would
      // silently accumulate work forever.
      return { status: 400, body: { title: `No sync endpoint registered for '${type}'` } }
    }

    if (!sender.perTransaction) return post(sender, batch)

    // One request each, results folded back into a single outcome. A hard
    // failure part-way through returns immediately so the whole batch retries;
    // anything already accepted is replayed, which the ship command treats as
    // Unchanged rather than a duplicate.
    const rejected: RejectedRow[] = []
    for (const transaction of batch) {
      const outcome = await post(sender, [transaction])
      const ok = outcome.status !== null && outcome.status >= 200 && outcome.status < 300
      if (!ok) return outcome
      rejected.push(...readRejectedRows(outcome.body))
    }
    return { status: 200, body: { rejected } }
  }
}

function readRejectedRows(body: unknown): RejectedRow[] {
  if (!body || typeof body !== 'object') return []
  const rows = (body as { rejected?: unknown }).rejected
  return Array.isArray(rows) ? rows as RejectedRow[] : []
}
